import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Trash2, Heart, RefreshCw, Clock, Search, ExternalLink, Shield, ShieldOff, CheckCheck, Loader2, ShieldX, Edit3, Eraser, Check, X } from 'lucide-react';
import { redactSelection } from '../../utils/redactSelection';
import Redacted from '../Redacted';

const API = process.env.REACT_APP_BACKEND_URL;

/** Inline redact/edit editor for a single moderation-row comment.
 *  Kept as a child component so per-row state doesn't leak across the
 *  list and every row has its own textarea ref (needed for
 *  selectionStart/End reads by `redactSelection`). */
const CommentEditor = ({ comment, token, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.comment_text || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);

  useEffect(() => { setDraft(comment.comment_text || ''); }, [comment.comment_text]);

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${API}/api/admin/article-comments/${comment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment_text: draft }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      onSaved?.(draft);
      setEditing(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mb-3">
        <p className="text-gray-200 text-sm whitespace-pre-wrap break-words">
          <Redacted text={comment.comment_text} testId={`article-mod-body-${comment.id}`} />
        </p>
        <button
          type="button"
          onClick={() => { setEditing(true); setDraft(comment.comment_text || ''); setErr(''); }}
          data-testid={`article-mod-edit-${comment.id}`}
          className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-yellow-700/50 hover:border-yellow-500 text-yellow-300 text-[10px] font-black uppercase tracking-widest"
        >
          <Edit3 className="h-3 w-3" /> Edit / Redact
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3 space-y-2">
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        maxLength={2000}
        data-testid={`article-mod-textarea-${comment.id}`}
        className="w-full bg-black border border-yellow-700/60 focus:border-yellow-400 rounded p-2 text-sm text-white outline-none resize-y"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => {
            const next = redactSelection(ref.current, draft);
            if (next != null) { setDraft(next); setErr(''); }
            else setErr('Highlight the word or name you want to redact first.');
          }}
          data-testid={`article-mod-redact-selection-${comment.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-yellow-700/50 hover:border-yellow-500 text-yellow-300 text-[10px] font-black uppercase tracking-widest"
          title="Highlight a name then click to whiteout it"
        >
          <Eraser className="h-3 w-3" /> Redact selection
        </button>
        <p className="text-[10px] text-gray-500">
          or type <span className="font-mono font-black text-yellow-300">XXXX</span> manually · {draft.length}/2000
        </p>
      </div>
      {err && <p className="text-[11px] text-red-400">{err}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !draft.trim()}
          data-testid={`article-mod-save-${comment.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[10px] font-black uppercase tracking-widest"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Save
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setErr(''); setDraft(comment.comment_text || ''); }}
          data-testid={`article-mod-cancel-${comment.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
        >
          <X className="h-3 w-3" /> Cancel
        </button>
      </div>
    </div>
  );
};

const ArticleCommentsModerationTab = ({ token }) => {
  const [data, setData] = useState({ total: 0, comments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [busyIp, setBusyIp] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all|pending|approved|rejected

  const statusCounts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    (data.comments || []).forEach((row) => {
      const s = row.status || 'approved';
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [data.comments]);

  const pendingCount = statusCounts.pending;

  const filteredComments = useMemo(() => {
    if (statusFilter === 'all') return data.comments || [];
    return (data.comments || []).filter((c) => (c.status || 'approved') === statusFilter);
  }, [data.comments, statusFilter]);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API}/api/admin/article-comments`);
      if (query.trim()) url.searchParams.set('q', query.trim());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load article comments');
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load article comments');
    } finally {
      setLoading(false);
    }
  }, [query, token]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const approveAllPending = async () => {
    if (!window.confirm(
      `Approve ALL pending article comments so they show up publicly?\n\n` +
      `This is a one-shot cleanup for anything already stuck in the moderation queue.\n` +
      `New comments already auto-post - this is only needed once.`,
    )) return;
    setBulkBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/article-comments/approve-all-pending`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || 'Bulk approve failed');
      window.alert(`Approved ${json.approved} comment${json.approved === 1 ? '' : 's'}.`);
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const hardDelete = async (c) => {
    const msg = c.is_reply
      ? `Permanently delete this reply and any deeper replies underneath it?\nThis cannot be undone.`
      : `Permanently delete this comment and ALL its replies?\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusyId(c.id);
    try {
      const res = await fetch(`${API}/api/admin/article-comments/${c.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const blockIp = async (ip, commentId) => {
    if (!ip) return;
    setBusyIp(ip);
    try {
      const res = await fetch(`${API}/api/admin/blocked-ips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ip,
          reason: `Banned from moderation panel${commentId ? ` · commentId:${commentId}` : ''}`,
        }),
      });
      if (!res.ok) throw new Error('Block failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyIp(null);
    }
  };

  const banAndDeleteThread = async (c) => {
    if (!c.ip) return;
    if (!window.confirm(
      `NUKE this poster?\n\n` +
      `• Ban IP ${c.ip} from posting anywhere on the site\n` +
      `• Delete this ${c.is_reply ? 'reply' : 'thread and every reply under it'}\n\n` +
      `Cannot be undone.`,
    )) return;
    setBusyIp(c.ip);
    setBusyId(c.id);
    try {
      // Ban first so no new posts can land while we delete.
      const b = await fetch(`${API}/api/admin/blocked-ips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ip: c.ip,
          reason: `Nuked from moderation panel · commentId:${c.id}`,
        }),
      });
      if (!b.ok && b.status !== 409) throw new Error('Ban failed');
      const d = await fetch(`${API}/api/admin/article-comments/${c.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!d.ok) throw new Error('Delete failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyIp(null);
      setBusyId(null);
    }
  };

  const unblockIp = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`Unblock IP ${ip}? They'll be able to post again.`)) return;
    setBusyIp(ip);
    try {
      const res = await fetch(`${API}/api/admin/blocked-ips/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Unblock failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyIp(null);
    }
  };

  const formatTime = useMemo(() => (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
    } catch {
      return iso;
    }
  }, []);

  const onSubmit = (e) => { e.preventDefault(); fetchComments(); };

  return (
    <div className="space-y-5" data-testid="article-comments-moderation-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-black uppercase tracking-widest text-red-400">
            Article Comments
          </span>
          <span className="bg-red-900/40 border border-red-700/40 text-red-200 text-[10px] font-bold px-2 py-0.5 rounded tabular-nums">
            {data.total}
          </span>
        </div>
        <button
          onClick={fetchComments}
          data-testid="article-comments-refresh-btn"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <form onSubmit={onSubmit} className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[180px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search comment text, author, or article title..."
            data-testid="article-comments-search-input"
            className="w-full bg-zinc-900 border border-red-900/30 rounded pl-10 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
          />
        </div>
        <button
          type="submit"
          data-testid="article-comments-search-btn"
          className="bg-red-600 hover:bg-red-500 text-white font-bold px-4 py-2 rounded text-sm transition-colors"
        >
          Search
        </button>
        {pendingCount > 0 && (
          <button
            type="button"
            onClick={approveAllPending}
            disabled={bulkBusy}
            data-testid="article-comments-approve-all-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold px-3 py-2 rounded text-sm transition-colors"
            title="Approve every currently-pending article comment"
          >
            {bulkBusy
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <CheckCheck className="h-3.5 w-3.5" />}
            Approve {pendingCount} pending
          </button>
        )}
      </form>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="article-comments-filters">
        {[
          { id: 'all',      label: 'All',      count: (data.comments || []).length, tone: 'red' },
          { id: 'pending',  label: 'Pending',  count: statusCounts.pending,          tone: 'yellow' },
          { id: 'approved', label: 'Approved', count: statusCounts.approved,         tone: 'emerald' },
          { id: 'rejected', label: 'Rejected', count: statusCounts.rejected,         tone: 'zinc' },
        ].map((f) => {
          const active = statusFilter === f.id;
          const activeTone = {
            red:     'bg-red-600 border-red-500 text-white',
            yellow:  'bg-yellow-600 border-yellow-500 text-white',
            emerald: 'bg-emerald-600 border-emerald-500 text-white',
            zinc:    'bg-zinc-700 border-zinc-600 text-white',
          }[f.tone];
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              disabled={f.id !== 'all' && f.count === 0}
              data-testid={`article-comments-filter-${f.id}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active
                  ? activeTone
                  : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:text-white hover:border-zinc-700'
              }`}
            >
              {f.label}
              <span className={`ml-1 rounded px-1 text-[9px] ${active ? 'bg-black/30 text-white' : 'bg-zinc-800 text-gray-500'}`}>
                {f.count}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm" data-testid="article-comments-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="article-comments-loading">Loading comments...</div>
      ) : filteredComments.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="article-comments-empty">
          {(data.comments || []).length === 0
            ? `No article comments ${query ? `matching "${query}"` : 'yet'}.`
            : `No ${statusFilter} comments in the current list.`}
        </div>
      ) : (
        <div className="space-y-3" data-testid="article-comments-list">
          {filteredComments.map((c) => (
            <div
              key={c.id}
              data-testid={`article-mod-comment-${c.id}`}
              className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-red-400 font-black text-sm">{c.author_name || 'Anonymous'}</span>
                  {c.is_reply && (
                    <span className="text-[10px] font-black tracking-widest uppercase text-purple-300 bg-purple-500/10 border border-purple-500/40 px-1.5 py-0.5 rounded">
                      replying to {c.parent_author || 'deleted'}
                    </span>
                  )}
                  <span className="text-gray-500 text-xs">on</span>
                  {c.article_slug ? (
                    <a
                      href={`/stories/${c.article_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white font-bold text-sm hover:text-red-400 inline-flex items-center gap-1 truncate max-w-[240px] sm:max-w-md"
                      data-testid={`article-mod-article-link-${c.id}`}
                    >
                      <span className="truncate">{c.article_title}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  ) : (
                    <span className="text-white font-bold text-sm">{c.article_title}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
                  <span className="flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {c.likes || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {formatTime(c.created_at)}
                  </span>
                </div>
              </div>

              <CommentEditor
                comment={c}
                token={token}
                onSaved={(newText) => {
                  setData((prev) => ({
                    ...prev,
                    comments: (prev.comments || []).map((row) =>
                      row.id === c.id ? { ...row, comment_text: newText } : row,
                    ),
                  }));
                }}
              />

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                  {c.ip ? (
                    <span
                      className={`px-2 py-1 rounded border ${
                        c.ip_blocked
                          ? 'bg-red-500/10 border-red-500/40 text-red-300'
                          : 'bg-zinc-900 border-zinc-700 text-gray-400'
                      }`}
                      data-testid={`article-mod-ip-${c.id}`}
                    >
                      IP {c.ip}{c.ip_blocked ? ' • blocked' : ''}
                    </span>
                  ) : null}
                  {c.ip && !c.ip_blocked && (
                    <button
                      onClick={() => blockIp(c.ip, c.id)}
                      disabled={busyIp === c.ip}
                      data-testid={`article-mod-block-ip-${c.id}`}
                      title={`One-click ban of ${c.ip} - no confirm, click again to unblock`}
                      className="inline-flex items-center gap-1 bg-yellow-900/30 hover:bg-yellow-800/50 text-yellow-200 font-bold px-2 py-1 rounded disabled:opacity-50 transition-colors"
                    >
                      {busyIp === c.ip
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Shield className="h-3 w-3" />}
                      Ban IP
                    </button>
                  )}
                  {c.ip && c.ip_blocked && (
                    <button
                      onClick={() => unblockIp(c.ip)}
                      disabled={busyIp === c.ip}
                      data-testid={`article-mod-unblock-ip-${c.id}`}
                      className="inline-flex items-center gap-1 bg-emerald-900/30 hover:bg-emerald-800/50 text-emerald-200 font-bold px-2 py-1 rounded disabled:opacity-50 transition-colors"
                    >
                      <ShieldOff className="h-3 w-3" /> Unblock
                    </button>
                  )}
                  {c.ip && !c.ip_blocked && (
                    <button
                      onClick={() => banAndDeleteThread(c)}
                      disabled={busyIp === c.ip || busyId === c.id}
                      data-testid={`article-mod-nuke-${c.id}`}
                      title="Ban IP AND delete this comment/thread"
                      className="inline-flex items-center gap-1 bg-red-900/40 hover:bg-red-800/60 text-red-200 font-bold px-2 py-1 rounded disabled:opacity-50 transition-colors"
                    >
                      <ShieldX className="h-3 w-3" /> Nuke
                    </button>
                  )}
                </div>

                <button
                  onClick={() => hardDelete(c)}
                  disabled={busyId === c.id}
                  data-testid={`article-mod-delete-${c.id}`}
                  className="flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 text-red-200 font-bold text-xs px-3 py-1.5 rounded disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete{c.is_reply ? '' : ' thread'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleCommentsModerationTab;
